import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link2, FileText, ClipboardList, Package, Truck, Wrench, ChevronRight } from 'lucide-react';

/**
 * iter100 — DocumentChainStrip
 *
 * One-line breadcrumb showing every related doc for the current anchor.
 * Clickable chips navigate to the related doc's detail page.
 *
 * Props:
 *   api        — authed axios factory (from AuthContext)
 *   docType    — "sales_order" | "production_order" | "packing_slip" | "bill_of_lading" | "repair_order"
 *   docId      — the current doc's id
 */
const META = {
  sales_order:      { icon: FileText,     path: '/orders/sales',   label: 'SO',  color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  production_order: { icon: ClipboardList, path: '/orders/production', label: 'PO', color: 'bg-sky-50 text-sky-700 border-sky-200' },
  packing_slip:     { icon: Package,      path: '/orders/packing', label: 'PS',  color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  bill_of_lading:   { icon: Truck,        path: '/orders/bol',     label: 'BoL', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  repair_order:     { icon: Wrench,       path: '/orders/repairs', label: 'RO',  color: 'bg-rose-50 text-rose-700 border-rose-200' },
  work_order:       { icon: Wrench,       path: '/orders/work',    label: 'WO',  color: 'bg-slate-50 text-slate-700 border-slate-200' },
};

const DISPLAY_ORDER = ['sales_order', 'production_order', 'work_order', 'packing_slip', 'bill_of_lading', 'repair_order'];

export const DocumentChainStrip = ({ api, docType, docId }) => {
  const nav = useNavigate();
  const [chain, setChain] = useState(null);
  const load = useCallback(async () => {
    if (!docId || docId === 'new') return;
    try {
      const r = await api().get(`/doc-chain/${docType}/${docId}`);
      setChain(r.data || null);
    } catch { /* non-blocking */ }
  }, [api, docType, docId]);
  useEffect(() => { load(); }, [load]);
  if (!chain) return null;
  // Flatten all siblings (anything that isn't the anchor) across every bucket, keep display order
  const items = [];
  DISPLAY_ORDER.forEach(dt => (chain.chain[dt] || []).forEach(d => { if (d.id !== docId) items.push(d); }));
  if (!items.length) return null;
  return (
    <div className="industrial-card border rounded-sm p-2 flex items-center gap-2 overflow-x-auto" data-testid="doc-chain-strip">
      <div className="flex items-center gap-1 shrink-0 text-xs text-slate-500 px-2">
        <Link2 className="w-3.5 h-3.5" />
        <span className="uppercase tracking-wider">Chain</span>
      </div>
      {items.map((d, i) => {
        const m = META[d.doc_type] || META.sales_order;
        const Icon = m.icon;
        return (
          <React.Fragment key={d.id}>
            {i > 0 && <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" />}
            <button
              onClick={() => nav(`${m.path}/${d.id}`)}
              className={`inline-flex items-center gap-1.5 border rounded-sm px-2 py-1 text-xs hover:opacity-80 whitespace-nowrap ${m.color}`}
              data-testid={`chain-chip-${d.id}`}
              title={`${d.customer_name || ''} · ${d.status || ''} · ${d.date || ''}`}
            >
              <Icon className="w-3 h-3" />
              <span className="font-semibold">{m.label}</span>
              <span className="mono">{d.external_number || d.number}</span>
              {d.status && <span className="text-[10px] uppercase tracking-wider opacity-70">· {d.status}</span>}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default DocumentChainStrip;
